import React from 'react';
import clsx from 'clsx';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** card = white circle w/ shadow · brand = plum scan button · onDark = translucent on camera/plum */
    variant?: 'plain' | 'card' | 'danger' | 'brand' | 'onDark' | 'soft' | 'accent' | 'sunken';
    /** 36 / 40 / 44 / 52 px */
    size?: 'sm' | 'xs' | 'md' | 'lg';
    label: string;
}

const variants: Record<NonNullable<IconButtonProps['variant']>, string> = {
    plain: 'text-ink-secondary hover:bg-surface-sunken hover:text-ink-primary',
    card: 'bg-surface-card shadow-sm text-ink-secondary hover:text-ink-primary',
    sunken: 'bg-surface-sunken text-ink-secondary hover:text-ink-primary',
    danger: 'bg-surface-card shadow-sm text-status-danger',
    brand: 'bg-surface-brand text-white shadow-brand hover:bg-primary-700',
    onDark: 'bg-white/[.12] text-white hover:bg-white/20',
    soft: 'bg-brandsoft text-ink-brand hover:bg-primary-100',
    accent: 'bg-accent-500 text-ink-onAccent shadow-accent hover:bg-accent-600',
};

const sizes: Record<NonNullable<IconButtonProps['size']>, string> = {
    sm: 'w-9 h-9 [&>svg]:w-[17px] [&>svg]:h-[17px]',
    xs: 'w-10 h-10 [&>svg]:w-[18px] [&>svg]:h-[18px]',
    md: 'w-11 h-11 [&>svg]:w-5 [&>svg]:h-5',
    lg: 'w-[52px] h-[52px] [&>svg]:w-[22px] [&>svg]:h-[22px]',
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ variant = 'plain', size = 'md', label, className, children, type = 'button', ...props }, ref) => (
        <button
            ref={ref}
            type={type}
            aria-label={label}
            title={label}
            className={clsx(
                'relative inline-flex shrink-0 items-center justify-center rounded-full transition-[background-color,transform] duration-200 active:scale-[.92] disabled:opacity-40 disabled:pointer-events-none',
                variants[variant],
                sizes[size],
                className,
            )}
            {...props}
        >
            {children}
        </button>
    ),
);
IconButton.displayName = 'IconButton';
export default IconButton;
